import React, { useState, useEffect } from "react";
import AdminTable from "../../Common/AdminTable";
import AdminDrawer from "../../Common/AdminDrawer";
import {
  getQuestionReportsList,
  getQuestionById,
  resolveQuestionReport,
} from "../../../../services/adminService";
import { useAdminAuth } from "../../../../contexts/AdminAuthContext";
import { Eye, CheckCircle, XCircle, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function QuestionReportsPage() {
  const { adminUser } = useAdminAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");

  const [activeReport, setActiveReport] = useState(null);
  const [relatedQuestion, setRelatedQuestion] = useState(null);

  // Correction form state
  const [resolutionAction, setResolutionAction] = useState("resolve"); // resolve or reject
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [correctKey, setCorrectKey] = useState("A");
  const [correctedExplanation, setCorrectedExplanation] = useState("");
  const [toastMessage, setToastMessage] = useState("");

  const loadReports = async () => {
    setLoading(true);
    try {
      const data = await getQuestionReportsList(statusFilter);
      setReports(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [statusFilter]);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3500);
  };

  const handleOpenReportDrawer = async (report) => {
    setActiveReport(report);
    setResolutionNotes(report.resolution_notes || "");
    try {
      const q = await getQuestionById(report.question_id);
      setRelatedQuestion(q);
      setCorrectKey(q.correct_answer || "A");
      setCorrectedExplanation(q.explanation || "");
    } catch (err) {
      setRelatedQuestion(null);
    }
  };

  const handleSubmitResolution = async () => {
    if (!activeReport) return;
    if (!resolutionNotes.trim()) {
      alert("Please provide resolution notes for the audit trail.");
      return;
    }

    try {
      const questionUpdates =
        resolutionAction === "resolve" && relatedQuestion
          ? {
              correct_answer: correctKey,
              explanation: correctedExplanation,
            }
          : null;

      await resolveQuestionReport(
        activeReport.id,
        {
          action: resolutionAction,
          resolutionNotes,
          questionUpdates,
        },
        adminUser.email
      );

      showToast(`Report ${activeReport.id} marked as ${resolutionAction === "resolve" ? "Resolved" : "Rejected"}`);
      setActiveReport(null);
      await loadReports();
    } catch (err) {
      console.error(err);
      alert("Failed to submit resolution: " + err.message);
    }
  };

  const columns = [
    {
      title: "Report ID",
      key: "id",
      width: "110px",
      render: (val) => <span style={{ fontWeight: 700, color: "#dc2626" }}>{val}</span>,
    },
    {
      title: "Question ID",
      key: "question_id",
      width: "120px",
      render: (val) => <span style={{ fontWeight: 600, color: "#0284c7" }}>{val}</span>,
    },
    {
      title: "Reporting Reason",
      key: "reason",
      render: (val, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{val}</div>
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              maxWidth: "320px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.user_comment}
          >
            "{row.user_comment}"
          </div>
        </div>
      ),
    },
    {
      title: "Reported By",
      key: "reporter_email",
      width: "180px",
      render: (val) => <span style={{ fontSize: "12.5px", color: "#475569" }}>{val}</span>,
    },
    {
      title: "Report Date",
      key: "created_at",
      width: "140px",
      render: (val) => (
        <span style={{ fontSize: "12px", color: "#64748b" }}>
          {new Date(val).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "100px",
      render: (val) => {
        const cls = (val || "").toLowerCase();
        return <span className={`status-badge ${cls}`}>{val}</span>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "100px",
      render: (_, row) => (
        <button
          className="admin-btn admin-btn-secondary admin-btn-sm"
          onClick={() => handleOpenReportDrawer(row)}
        >
          <Eye size={13} /> Review
        </button>
      ),
    },
  ];

  return (
    <div>
      {/* Toast Notification */}
      {toastMessage && (
        <div
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            background: "#0f172a",
            color: "#ffffff",
            padding: "12px 20px",
            borderRadius: "8px",
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            zIndex: 2000,
            fontSize: "14px",
          }}
        >
          <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
          {toastMessage}
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="admin-card" style={{ marginBottom: "16px", padding: "14px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span style={{ fontSize: "13px", fontWeight: 600, color: "#475569" }}>
            Filter by Status:
          </span>
          {["All", "Pending", "Resolved", "Rejected"].map((s) => (
            <button
              key={s}
              className={`admin-btn admin-btn-sm ${
                statusFilter === s ? "admin-btn-primary" : "admin-btn-secondary"
              }`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Reports Table */}
      <AdminTable
        columns={columns}
        data={reports}
        searchPlaceholder="Search reports by reason, question ID, reporter..."
        pageSize={10}
        rowKey="id"
      />

      {/* Review & Correction Drawer */}
      <AdminDrawer
        isOpen={Boolean(activeReport)}
        onClose={() => setActiveReport(null)}
        title={activeReport ? `Review Report: ${activeReport.id}` : ""}
        width="650px"
        footer={
          activeReport && activeReport.status === "Pending" ? (
            <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setActiveReport(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-danger"
                onClick={() => {
                  setResolutionAction("reject");
                  handleSubmitResolution();
                }}
              >
                <XCircle size={15} /> Reject Report
              </button>
              <button
                type="button"
                className="admin-btn admin-btn-primary"
                onClick={() => {
                  setResolutionAction("resolve");
                  handleSubmitResolution();
                }}
              >
                <CheckCircle size={15} /> Resolve & Apply Correction
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => setActiveReport(null)}
            >
              Close
            </button>
          )
        }
      >
        {activeReport && (
          <div>
            {/* User Report Details */}
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <AlertTriangle size={18} style={{ color: "#dc2626" }} />
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#991b1b" }}>
                  Issue Reported: {activeReport.reason}
                </span>
              </div>
              <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#7f1d1d" }}>
                "{activeReport.user_comment}"
              </p>
              <div style={{ fontSize: "11px", color: "#991b1b" }}>
                Reported by: <strong>{activeReport.reporter_email}</strong> on{" "}
                {new Date(activeReport.created_at).toLocaleString()}
              </div>
            </div>

            {/* Related Question Display & Correction Form */}
            {relatedQuestion ? (
              <div>
                <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "10px" }}>
                  Original Question ({relatedQuestion.id} &bull; v{relatedQuestion.version || 1})
                </h4>

                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid var(--admin-border)",
                    borderRadius: "8px",
                    padding: "14px",
                    marginBottom: "16px",
                    fontSize: "13px",
                    lineHeight: "1.5",
                  }}
                >
                  <p style={{ fontWeight: 600, color: "#1e293b", margin: "0 0 10px 0" }}>
                    {relatedQuestion.question}
                  </p>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
                    {["A", "B", "C", "D"].map((key) => (
                      <div
                        key={key}
                        style={{
                          padding: "6px 10px",
                          borderRadius: "4px",
                          background: relatedQuestion.correct_answer === key ? "#dcfce7" : "#ffffff",
                          border: `1px solid ${relatedQuestion.correct_answer === key ? "#86efac" : "#e2e8f0"}`,
                          fontSize: "12px",
                        }}
                      >
                        <strong>{key}:</strong> {relatedQuestion.options?.[key]}
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: "12px", color: "#475569" }}>
                    <strong>Current Answer Key:</strong> {relatedQuestion.correct_answer}
                  </div>
                </div>

                {activeReport.status === "Pending" && (
                  <div style={{ borderTop: "1px solid var(--admin-border)", paddingTop: "16px" }}>
                    <h4 style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b", marginBottom: "12px" }}>
                      Correction & Resolution Form
                    </h4>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Corrected Answer Key</label>
                      <select
                        className="admin-form-select"
                        value={correctKey}
                        onChange={(e) => setCorrectKey(e.target.value)}
                      >
                        <option value="A">Option A</option>
                        <option value="B">Option B</option>
                        <option value="C">Option C</option>
                        <option value="D">Option D</option>
                      </select>
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Corrected / Enhanced Explanation</label>
                      <textarea
                        className="admin-form-textarea"
                        rows={3}
                        value={correctedExplanation}
                        onChange={(e) => setCorrectedExplanation(e.target.value)}
                      />
                    </div>

                    <div className="admin-form-group">
                      <label className="admin-form-label">Resolution Notes (Recorded in Audit Trail) *</label>
                      <input
                        type="text"
                        className="admin-form-input"
                        placeholder="e.g. Corrected answer key from C to B after verifying with 2021 UPSC official key"
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {activeReport.status !== "Pending" && (
                  <div
                    style={{
                      background: "#f1f5f9",
                      padding: "14px",
                      borderRadius: "6px",
                      border: "1px solid var(--admin-border)",
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: "13px", color: "#334155", marginBottom: "4px" }}>
                      Resolution Record:
                    </div>
                    <div style={{ fontSize: "13px", color: "#475569" }}>
                      {activeReport.resolution_notes || "No notes entered."}
                    </div>
                    <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "6px" }}>
                      Resolved by: {activeReport.resolved_by} &bull; {new Date(activeReport.updated_at).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: "#ef4444", fontSize: "13px" }}>
                Associated Question was not found in the question bank.
              </div>
            )}
          </div>
        )}
      </AdminDrawer>
    </div>
  );
}
